/**
 * PresenceManager
 * High-level wrapper that combines PresenceClient with automatic features
 * Provides a simpler API than managing individual components
 * Matches the functionality of the working test app
 */
import { PresenceClient } from './PresenceClient';
import { AutoHeartbeatManager } from './AutoHeartbeatManager';
import { ActivityTracker } from './ActivityTracker';
import { IdleDetector } from './IdleDetector';
import { ConnectionState } from '../shared/types';
/**
 * All-in-one presence manager with automatic features
 * Simplifies presence integration by managing all components automatically
 */
export class PresenceManager {
    /**
     * Create PresenceManager
     * @param hubUrl - PresenceHub URL (e.g., 'http://localhost:5201/presencehub')
     * @param getToken - Function that returns JWT token
     * @param options - Configuration options
     */
    constructor(hubUrl, getToken, options = {}) {
        this.unsubscribeHandlers = [];
        // Merge with defaults
        this.options = {
            autoHeartbeat: options.autoHeartbeat !== false,
            heartbeatInterval: options.heartbeatInterval || 30,
            activityTracking: options.activityTracking !== false,
            activityDebounce: options.activityDebounce || 5000,
            highSignificanceThrottle: options.highSignificanceThrottle || 2000,
            lowSignificanceThrottle: options.lowSignificanceThrottle || 5000,
            idleDetection: options.idleDetection !== false,
            idleTimeout: options.idleTimeout || 5,
            idleGracePeriod: options.idleGracePeriod ?? 30,
            platform: options.platform || 'web',
            ...options,
        };
        // Create core client - map platform for PresenceClient (web or react-native only)
        const clientPlatform = this.options.platform === 'node' ? 'web' : this.options.platform;
        const clientOptions = {
            ...options,
            platform: clientPlatform,
        };
        this.client = new PresenceClient(hubUrl, getToken, clientOptions);
        // Create automatic feature managers
        this.heartbeat = new AutoHeartbeatManager(this.client, this.options.heartbeatInterval);
        this.activityTracker = new ActivityTracker(this.client, this.options.activityDebounce, this.options.highSignificanceThrottle, this.options.lowSignificanceThrottle, this.options.platform);
        this.idleDetector = new IdleDetector(this.client, this.activityTracker, this.options.idleTimeout, this.options.idleGracePeriod);
        // Setup automatic feature activation on connection
        this.setupAutoFeatures();
    }
    /**
     * Setup automatic features to start/stop with connection
     * @private
     */
    setupAutoFeatures() {
        const unsubscribe = this.client.onStateChange((state) => {
            if (state === ConnectionState.Connected) {
                console.log('[PresenceManager] Connected - starting automatic features');
                // Start heartbeat if enabled
                if (this.options.autoHeartbeat) {
                    this.heartbeat.start();
                }
                // Start activity tracking if enabled
                if (this.options.activityTracking) {
                    this.activityTracker.start();
                }
                // Start idle detection if enabled
                if (this.options.idleDetection) {
                    this.idleDetector.start();
                }
            }
            else if (state === ConnectionState.Disconnected) {
                console.log('[PresenceManager] Disconnected - stopping automatic features');
                // Stop all automatic features
                this.heartbeat.stop();
                this.activityTracker.stop();
                this.idleDetector.stop();
            }
        });
        this.unsubscribeHandlers.push(unsubscribe);
    }
    // ===== Connection Methods =====
    /**
     * Connect to PresenceHub
     * Automatically starts enabled automatic features
     */
    async connect() {
        await this.client.connect();
    }
    /**
     * Disconnect from PresenceHub
     * Automatically stops all automatic features
     */
    async disconnect() {
        await this.client.disconnect();
    }
    /**
     * Get connection state
     */
    getConnectionState() {
        return this.client.getState();
    }
    /**
     * Check if connected
     */
    isConnected() {
        return this.client.getState() === ConnectionState.Connected;
    }
    // ===== User Presence Methods =====
    /**
     * Update current user's status
     * @param status - Presence status
     * @param message - Optional activity message
     */
    async updateStatus(status, message) {
        await this.client.updateStatus(status, message);
    }
    /**
     * Get list of online user IDs
     */
    async getOnlineUsers() {
        return await this.client.getOnlineUsers();
    }
    /**
     * Get specific user's presence
     */
    async getUserPresence(userId) {
        return await this.client.getUserPresence(userId);
    }
    /**
     * Get all presence records (online + recent offline)
     */
    async getAllPresence() {
        return await this.client.getAllPresence();
    }
    /**
     * Get current user's presence
     */
    getCurrentUserPresence() {
        return this.client.getCurrentUserPresence();
    }
    /**
     * Get online users as a map
     */
    getOnlineUsersMap() {
        return this.client.getOnlineUsersMap();
    }
    // ===== Admin Methods =====
    /**
     * Get presence statistics (Admin only)
     */
    async getPresenceStats() {
        return await this.client.getPresenceStats();
    }
    /**
     * Set all users offline (Admin only)
     */
    async setAllUsersOffline() {
        await this.client.setAllUsersOffline();
    }
    /**
     * Force disconnect a user (Admin only)
     */
    async forceDisconnectUser(userId) {
        await this.client.forceDisconnectUser(userId);
    }
    // ===== Event Subscriptions =====
    /**
     * Subscribe to presence events
     * @param event - Event name
     * @param handler - Event handler function
     * @returns Unsubscribe function
     */
    on(event, handler) {
        return this.client.on(event, handler);
    }
    /**
     * Subscribe to idle state changes
     * @param callback - Called when idle state changes
     */
    onIdleChange(callback) {
        this.idleDetector.stop(); // Stop current if running
        this.idleDetector.start(callback); // Restart with new callback
    }
    // ===== Configuration Methods =====
    /**
     * Enable/disable automatic heartbeat
     */
    setAutoHeartbeat(enabled) {
        this.options.autoHeartbeat = enabled;
        if (enabled && this.isConnected()) {
            this.heartbeat.start();
        }
        else {
            this.heartbeat.stop();
        }
    }
    /**
     * Update heartbeat interval
     */
    setHeartbeatInterval(seconds) {
        this.options.heartbeatInterval = seconds;
        this.heartbeat.setInterval(seconds);
    }
    /**
     * Enable/disable activity tracking
     */
    setActivityTracking(enabled) {
        this.options.activityTracking = enabled;
        if (enabled && this.isConnected()) {
            this.activityTracker.start();
        }
        else {
            this.activityTracker.stop();
        }
    }
    /**
     * Update activity debounce delay
     */
    setActivityDebounce(ms) {
        this.options.activityDebounce = ms;
        this.activityTracker.setDebounce(ms);
    }
    /**
     * Enable/disable idle detection
     */
    setIdleDetection(enabled) {
        this.options.idleDetection = enabled;
        if (enabled && this.isConnected()) {
            this.idleDetector.start();
        }
        else {
            this.idleDetector.stop();
        }
    }
    /**
     * Update idle timeout
     */
    setIdleTimeout(minutes) {
        this.options.idleTimeout = minutes;
        this.idleDetector.setIdleTimeout(minutes);
    }
    /**
     * Update high-significance activity throttle
     */
    setHighSignificanceThrottle(ms) {
        this.options.highSignificanceThrottle = ms;
        this.activityTracker.setHighSignificanceThrottle(ms);
    }
    /**
     * Update low-significance activity throttle
     */
    setLowSignificanceThrottle(ms) {
        this.options.lowSignificanceThrottle = ms;
        this.activityTracker.setLowSignificanceThrottle(ms);
    }
    /**
     * Update idle grace period
     */
    setIdleGracePeriod(seconds) {
        this.options.idleGracePeriod = seconds;
        this.idleDetector.setGracePeriod(seconds);
    }
    // ===== Status Methods =====
    /**
     * Get last heartbeat timestamp
     */
    getLastHeartbeat() {
        return this.heartbeat.getLastHeartbeat();
    }
    /**
     * Get last activity timestamp
     */
    getLastActivity() {
        return new Date(this.activityTracker.getLastActivityTime());
    }
    /**
     * Get current idle status
     */
    isIdle() {
        return this.idleDetector.getIdleStatus();
    }
    /**
     * Get remaining time before going idle (in seconds)
     */
    getRemainingIdleTime() {
        return this.idleDetector.getRemainingTime();
    }
    /**
     * Get formatted remaining idle time string
     */
    getFormattedIdleTime() {
        return this.idleDetector.getFormattedRemainingTime();
    }
    // ===== Idle/Lock Management =====
    /**
     * Reset the idle timer after screen unlock
     * Resets both the activity tracker timestamp and idle detector state
     * This prevents immediate re-lock after password entry
     */
    resetIdleTimer() {
        // Reset activity tracker timestamp to now
        this.activityTracker.resetLastActivity();
        // Force idle state to false
        this.idleDetector.forceIdleState(false);
        console.log('[PresenceManager] Idle timer reset (activity + idle state)');
    }
    // ===== Cleanup =====
    /**
     * Destroy the manager and cleanup all resources
     */
    async destroy() {
        console.log('[PresenceManager] Destroying');
        // Stop all automatic features
        this.heartbeat.stop();
        this.activityTracker.stop();
        this.idleDetector.stop();
        // Unsubscribe from all handlers
        this.unsubscribeHandlers.forEach((unsub) => unsub());
        this.unsubscribeHandlers = [];
        // Disconnect and destroy client
        await this.client.destroy();
    }
}
//# sourceMappingURL=PresenceManager.js.map